import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import { usePublicUiLocale } from '../../contexts/PublicUiLocaleContext';
import HelpHint from '../../components/HelpHint';
import { formatMoney, formatUsd } from '../../project-modules/store/projectCostEstimate';

const LEDGER_TYPE_KEYS = {
  top_up: 'billingLedgerTopUp',
  project_initial: 'billingLedgerProjectInitial',
  project_renewal: 'billingLedgerRenewal',
  addon_prorated: 'billingLedgerAddon',
  adjustment: 'billingLedgerAdjustment',
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
}

function chargeLinesLabel(lines) {
  if (!lines?.length) return null;
  return lines.map((l) => `${l.label}: ${formatMoney(l.amount)}`).join(' · ');
}

export default function ProjectBillingPanel({ billing, accountSummary, projectSlug }) {
  const { sa } = usePublicUiLocale();

  const projectBilling = useMemo(() => {
    if (billing) return billing;
    const slug = projectSlug;
    if (slug && accountSummary?.projects) {
      return accountSummary.projects.find((p) => p.slug === slug) || null;
    }
    return null;
  }, [billing, accountSummary, projectSlug]);

  if (!projectBilling) return null;

  const {
    monthly_usd: monthly,
    billing_next_at: nextAt,
    billing_suspended: suspended,
    insufficient_for_renewal: renewalWarn,
    recent_charges: charges = [],
  } = projectBilling;

  const ledgerLabel = (type) => {
    const key = LEDGER_TYPE_KEYS[type];
    return key ? sa[key] : type;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="overline" color="text.secondary">
          {sa.billingProjectSection}
        </Typography>
        <HelpHint title={sa.helpBillingPeriodTitle} text={sa.helpBillingPeriod} />
      </Box>

      {suspended ? (
        <Alert severity="error" icon={<BlockIcon />} sx={{ mt: 1 }}>
          {sa.billingProjectSuspended}
        </Alert>
      ) : null}

      {!suspended && renewalWarn ? (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 1 }}>
          {sa.billingProjectRenewalWarning
            .replace('{amount}', formatUsd(monthly))
            .replace('{date}', nextAt || '—')}
        </Alert>
      ) : null}

      <Stack spacing={1} sx={{ mt: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">{sa.billingMonthlyCost}</Typography>
            <HelpHint title={sa.helpBillingMonthlyTitle} text={sa.helpBillingMonthly} />
          </Box>
          <Typography variant="body2" fontWeight={600}>{formatUsd(monthly)}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">{sa.billingNextCharge}</Typography>
            <HelpHint title={sa.helpBillingNextChargeTitle} text={sa.helpBillingNextCharge} />
          </Box>
          <Chip size="small" label={nextAt || sa.billingNextChargeUnknown} variant="outlined" />
        </Stack>
      </Stack>

      {charges.length > 0 ? (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="overline" color="text.secondary">
            {sa.billingRecentCharges}
          </Typography>
          <Stack spacing={1.25} sx={{ mt: 1 }}>
            {charges.map((c) => {
              const isCredit = Number(c.amount_usd) > 0;
              const detail = chargeLinesLabel(c.lines);
              return (
                <Box
                  key={c.id}
                  sx={{
                    py: 0.75,
                    px: 1,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {ledgerLabel(c.type)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(c.created_at)}
                      </Typography>
                      {detail ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                          {detail}
                        </Typography>
                      ) : null}
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={isCredit ? 'success.main' : 'text.primary'}
                      sx={{ flexShrink: 0 }}
                    >
                      {isCredit ? '+' : ''}{formatMoney(c.amount_usd)}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {sa.billingNoChargesYet}
        </Typography>
      )}
    </Box>
  );
}

ProjectBillingPanel.propTypes = {
  projectSlug: PropTypes.string,
  billing: PropTypes.shape({
    slug: PropTypes.string,
    monthly_usd: PropTypes.number,
    billing_next_at: PropTypes.string,
    billing_suspended: PropTypes.bool,
    insufficient_for_renewal: PropTypes.bool,
    recent_charges: PropTypes.arrayOf(PropTypes.object),
  }),
  accountSummary: PropTypes.object,
};

ProjectBillingPanel.defaultProps = {
  projectSlug: '',
  billing: null,
  accountSummary: null,
};
